import { useEffect, useMemo, useRef } from "react";
import { TransactionRuleGroup } from "@common/types/transactionRule";
import { useWorkspaceRuleGroupsList } from "@api/rules.api";
import { useActionsContext } from "./ActionsContext";
import { ActionRowRuleMatch } from "./types";
import {
    ActionRowRuleMatchMap,
    classifyActionRowRuleMatches,
} from "./ruleMatchClassification";

/**
 * For each row with `createRule=true`, look up rules in the selected group
 * that share the same normalized condition set and classify the row:
 *
 * - `merge` — same conditions, the existing rule has no action of this type.
 * - `skip_same_value` — same conditions, same action type, same value.
 * - `conflict_same_type_diff_value` — same conditions, same action type,
 *   different value. Must be resolved by the conflict modal at confirm time.
 *
 * The classification is written back onto each row via `updateRow` so the
 * confirm flow and the modal can consume it without re-running the diff.
 */
export function useRuleMatchClassification(): void {
    const { rows, ruleGroupId, scopeConditions, updateRow } =
        useActionsContext();
    const ruleGroupsList = useWorkspaceRuleGroupsList();

    const selectedGroup = useMemo<TransactionRuleGroup | undefined>(
        () => ruleGroupsList.data?.find((g) => g.id === ruleGroupId),
        [ruleGroupsList.data, ruleGroupId],
    );

    const matches = useMemo<ActionRowRuleMatchMap>(
        () =>
            classifyActionRowRuleMatches({
                rows,
                selectedGroup,
                scopeConditions,
            }),
        [rows, selectedGroup, scopeConditions],
    );

    // Track the last pushed classification per row so we only call `updateRow`
    // when it actually changed; this mirrors the previous error/merge effect.
    const lastRef = useRef<Record<string, string>>({});

    useEffect(() => {
        const ids = new Set([
            ...Object.keys(lastRef.current),
            ...Object.keys(matches),
        ]);
        for (const id of ids) {
            const current = matches[id] ?? null;
            const key = current ? matchKey(current) : "";
            if (lastRef.current[id] === key) {
                continue;
            }
            lastRef.current[id] = key;
            updateRow(id, { ruleMatch: current });
        }
    }, [matches, updateRow]);
}

function matchKey(m: ActionRowRuleMatch): string {
    return JSON.stringify(m);
}
